import axios from 'axios'
import logger from '@/utils/logger'

/**
 * Authentication Service for Olvidadme
 *
 * Implementing Enhanced Service Layer pattern from PATTERNS_FROM_BASKET_BASE.md
 * Connects with enterprise-ready backend auth system (8 endpoints)
 *
 * Enhanced Features (Task 2.2 - Auth Service Security Refactor):
 * - Enhanced race condition handling with timeout and error recovery
 * - Proactive CSRF token management with TTL lifecycle
 * - Streamlined security logging for GDPR audit trail (Task 3.2 Cleanup)
 * - Optimized interceptors with centralized logic
 * - Circuit breaker pattern for resilient API calls
 * - GDPR compliance headers and audit logging
 *
 * FASE 3 Cleanup (ADR-092):
 * - Removed over-engineered FrontendSecurityLogger class
 * - Simplified to standard logger for better maintainability
 * - Kept essential security audit logging for GDPR compliance
 */

// ==========================================
// SECURITY LOGGING (Task 3.2 - Simplified)
// ==========================================

/**
 * Simplified Security Logging for Authentication Events
 *
 * FASE 3 Cleanup (ADR-092): Removed over-engineered FrontendSecurityLogger
 * Now using standard logger from @/utils/logger for better maintainability
 * Maintains essential GDPR audit trail without unnecessary complexity
 */

// ==========================================
// AXIOS CLIENT CONFIGURATION
// ==========================================

// En runtime normal el cliente usa rutas relativas (/api), que apuntan al
// mismo origen. Durante prerender (build), Puppeteer inyecta la flag
// `window.__PRERENDER_API_TARGET` con la URL absoluta de producción para que
// los fetch de los componentes hidraten con datos reales antes del snapshot.
const API_BASE_URL =
  (typeof window !== 'undefined' && window.__PRERENDER_API_TARGET) || '/api'

// Create dedicated auth client
const authClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Create main API client
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
})

// ==========================================
// CSRF TOKEN MANAGEMENT
// ==========================================

let csrfToken = null
let csrfTokenExpiry = null
const CSRF_TOKEN_TTL = 30 * 60 * 1000 // 30 minutes

/**
 * Get current CSRF token or fetch new one if expired
 * Enhanced with proactive TTL management (Task 2.2)
 */
const getCsrfToken = async () => {
  try {
    // Check if current token is still valid (with buffer)
    const now = Date.now()
    const buffer = 5 * 60 * 1000 // 5 minute buffer

    if (csrfToken && csrfTokenExpiry && (now + buffer) < csrfTokenExpiry) {
      return csrfToken
    }

    logger.info('Fetching new CSRF token')
    const response = await authClient.get('/csrf-token')

    csrfToken = response.data.data.csrfToken
    csrfTokenExpiry = Date.now() + CSRF_TOKEN_TTL

    logger.info('CSRF token refreshed successfully', {
      expiresAt: new Date(csrfTokenExpiry).toISOString()
    })

    return csrfToken
  } catch (error) {
    logger.error('Failed to fetch CSRF token', {
      error: error.message,
      statusCode: error.response?.status
    })

    throw error
  }
}

/**
 * Clear CSRF token cache
 * Enhanced logging for security audit trail (Task 2.2)
 */
const clearCsrfToken = () => {
  csrfToken = null
  csrfTokenExpiry = null
  logger.info('CSRF token cache cleared')
}

// ==========================================
// CENTRALIZED REQUEST INTERCEPTOR LOGIC
// ==========================================

/**
 * Add CSRF token to request configuration
 * Centralized logic to reduce duplication (Task 2.2)
 */
const addCsrfTokenToRequest = async (config) => {
  if (['post', 'put', 'patch', 'delete'].includes(config.method.toLowerCase())) {
    try {
      const token = await getCsrfToken()
      config.headers['X-CSRF-Token'] = token

      logger.info('CSRF token added to request', {
        method: config.method.toUpperCase(),
        url: config.url,
        tokenExpiry: csrfTokenExpiry ? new Date(csrfTokenExpiry).toISOString() : null
      })
    } catch (error) {
      logger.warn('CSRF token required but unavailable', {
        method: config.method.toUpperCase(),
        url: config.url,
        error: error.message
      })
    }
  }

  return config
}

// ==========================================
// REQUEST INTERCEPTORS
// ==========================================

// Auth client interceptors (for authentication endpoints)
authClient.interceptors.request.use(
  async (config) => {
    logger.info('Auth client request initiated', {
      method: config.method.toUpperCase(),
      url: config.url
    })

    return await addCsrfTokenToRequest(config)
  },
  (error) => {
    logger.error('Auth client request error', {
      error: error.message
    })
    return Promise.reject(error)
  }
)

// API client interceptors (for protected endpoints)
apiClient.interceptors.request.use(
  async (config) => {
    logger.info('API client request initiated', {
      method: config.method.toUpperCase(),
      url: config.url
    })

    // Auth tokens now handled via httpOnly cookies automatically
    // No need for manual Authorization header management

    return await addCsrfTokenToRequest(config)
  },
  (error) => {
    logger.error('API client request error', {
      error: error.message
    })
    return Promise.reject(error)
  }
)

// ==========================================
// TOKEN REFRESH LOGIC
// ==========================================

let isRefreshing = false
let failedQueue = []
const REFRESH_TIMEOUT = 30000 // 30 seconds

const processQueue = (error, token = null) => {
  logger.info('Processing refresh queue', {
    queueSize: failedQueue.length,
    hasError: !!error,
    hasToken: !!token
  })

  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

/**
 * Enhanced token refresh with timeout and error recovery (Task 2.2)
 */
const performTokenRefresh = async () => {
  return new Promise(async (resolve, reject) => {
    const refreshTimeout = setTimeout(() => {
      isRefreshing = false
      logger.error('Token refresh timeout', { timeout: REFRESH_TIMEOUT })
      reject(new Error('Token refresh timeout'))
    }, REFRESH_TIMEOUT)

    try {
      const response = await authClient.post('/auth/refresh')
      clearTimeout(refreshTimeout)
      isRefreshing = false
      resolve(response.data)
    } catch (error) {
      clearTimeout(refreshTimeout)
      isRefreshing = false
      reject(error)
    }
  })
}

// API client response interceptor with enhanced error handling
apiClient.interceptors.response.use(
  (response) => {
    logger.info('API request successful', {
      method: response.config.method?.toUpperCase(),
      url: response.config.url,
      status: response.status
    })
    return response
  },
  async (error) => {
    const originalRequest = error.config
    const errorStatus = error.response?.status
    const errorCode = error.response?.data?.code

    logger.warn('API request failed', {
      status: errorStatus,
      url: originalRequest?.url,
      method: originalRequest?.method,
      errorCode
    })

    // Handle 401 Unauthorized - Token refresh
    if (errorStatus === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        logger.info('Queueing request while refresh in progress', {
          queueSize: failedQueue.length + 1
        })

        // Queue the request while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(() => {
          return apiClient(originalRequest)
        }).catch(err => {
          return Promise.reject(err)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      logger.info('Starting token refresh for 401 error')

      try {
        await performTokenRefresh()
        processQueue(null)
        logger.info('Token refresh successful, retrying original request')
        return apiClient(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        logger.error('Token refresh failed, redirecting to login', {
          error: refreshError.message
        })

        // Redirect to login
        window.location.href = '/auth/login'
        return Promise.reject(refreshError)
      }
    }

    // Handle 419 CSRF Token Mismatch
    if (errorStatus === 403 && (errorCode === 'CSRF_TOKEN_INVALID' || errorCode === 'CSRF_TOKEN_MISSING')) {
      if (!originalRequest._csrfRetry) {
        originalRequest._csrfRetry = true
        clearCsrfToken()

        logger.warn('CSRF token invalid, clearing cache and retrying')

        try {
          await addCsrfTokenToRequest(originalRequest)
          logger.info('Retrying request with fresh CSRF token')
          return apiClient(originalRequest)
        } catch (error) {
          logger.error('CSRF retry failed, giving up', {
            error: error.message
          })
        }
      }
    }

    // Enhanced error logging for security analysis
    if (errorStatus >= 400) {
      logger.error('HTTP error response', {
        status: errorStatus,
        method: originalRequest?.method,
        url: originalRequest?.url,
        errorCode,
        message: error.response?.data?.message
      })
    }

    return Promise.reject(error)
  }
)

// ==========================================
// AUTHENTICATION FUNCTIONS
// ==========================================

/**
 * User login with enhanced security logging (Task 2.2)
 */
const login = async (credentials) => {
  try {
    logger.info('User login attempt initiated', {
      email: credentials.email,
      rememberMe: credentials.rememberMe || false,
      userAgent: navigator.userAgent
    })

    const response = await authClient.post('/auth/login', credentials)

    // Access token now handled via httpOnly cookies
    // No need to store in memory or global variables

    logger.info('User login successful', {
      userId: response.data.data?.user?.id,
      email: credentials.email,
      sessionExpiry: response.data.data?.expiresIn
    })

    // Return the data structure that matches what auth store expects
    // Note: accessToken removed - handled via httpOnly cookies only
    return {
      user: response.data.data?.user,
      expiresIn: response.data.data?.expiresIn,
      message: response.data.message
    }
  } catch (error) {
    logger.error('User login failed', {
      email: credentials.email,
      error: error.message,
      statusCode: error.response?.status,
      errorCode: error.response?.data?.code
    })
    throw normalizeError(error, 'Error en login')
  }
}

/**
 * User registration with GDPR logging (Task 2.2)
 */
const register = async (userData) => {
  try {
    logger.info('User registration initiated', {
      email: userData.email,
      hasGdprConsent: !!userData.gdprConsent,
      autonomousCommunity: userData.address?.autonomousCommunity
    })

    const response = await authClient.post('/auth/register', userData)

    // No access token stored on registration - user needs email verification
    // Store access token for future requests would go here if auto-login was enabled

    logger.info('User registration successful', {
      userId: response.data.data?.user?.id,
      email: userData.email,
      requiresEmailVerification: true
    })

    // Return the full response for registration (includes success, message, data)
    return response.data
  } catch (error) {
    logger.error('User registration failed', {
      email: userData.email,
      error: error.message,
      statusCode: error.response?.status,
      errorCode: error.response?.data?.code
    })
    throw normalizeError(error, 'Error en registro')
  }
}

/**
 * User registration with testing endpoint (staging/development only)
 * Bypasses payment verification and allows direct tier assignment
 */
const registerTesting = async (userData) => {
  try {
    logger.info('Testing registration initiated', {
      email: userData.email,
      planTier: userData.planTier || 'default',
      hasGdprConsent: !!userData.gdprConsents,
      autonomousCommunity: userData.address?.autonomousCommunity
    })

    const response = await authClient.post('/auth/register-testing', userData)

    logger.info('Testing registration successful with immediate login', {
      userId: response.data.data?.user?.id,
      email: userData.email,
      subscriptionTier: response.data.data?.user?.subscriptionTier,
      bypassesApplied: response.data.data?.testingInfo?.bypassesApplied
    })

    // Return the full response (includes tokens for immediate login)
    return response.data
  } catch (error) {
    logger.error('Testing registration failed', {
      email: userData.email,
      error: error.message,
      statusCode: error.response?.status,
      errorCode: error.response?.data?.code
    })
    throw normalizeError(error, 'Error en registro de prueba')
  }
}

/**
 * Logout with cleanup and enhanced logging (Task 2.2)
 */
const logout = async () => {
  try {
    logger.info('Initiating user logout')
    await apiClient.post('/auth/logout')

    // Clear CSRF token cache using enhanced function
    clearCsrfToken()
    // Access token cleared automatically via httpOnly cookie

    logger.info('User logout completed successfully')
  } catch (error) {
    // Don't throw on logout errors - just log them
    logger.warn('Logout request failed', {
      error: error.message,
      status: error.response?.status
    })
  }
}

/**
 * Token refresh with enhanced retry logic (Task 2.2)
 */
const refreshTokens = async () => {
  return withRetry(async () => {
    logger.info('Token refresh attempt initiated')

    const response = await authClient.post('/auth/refresh')

    // Access token now handled via httpOnly cookies
    // No need to manually store tokens

    logger.info('Token refresh completed successfully')

    return response.data
  }, 2, 1500) // Max 2 retries with 1.5s base delay
    .catch(error => {
      logger.error('Token refresh failed after retries', {
        error: error.message,
        statusCode: error.response?.status
      })
      throw normalizeError(error, 'Error al renovar token')
    })
}

/**
 * Get user profile with enhanced logging (Task 2.2)
 */
const getProfile = async () => {
  try {
    logger.info('Profile retrieval initiated')

    const response = await apiClient.get('/auth/profile')

    logger.info('Profile retrieved successfully', {
      userId: response.data.data?.user?.id
    })

    return response.data
  } catch (error) {
    logger.error('Profile retrieval failed', {
      error: error.message,
      statusCode: error.response?.status
    })
    throw normalizeError(error, 'Error al obtener perfil')
  }
}

/**
 * Update user profile with enhanced logging (Task 2.2)
 */
const updateProfile = async (profileData) => {
  try {
    logger.info('Profile update initiated', {
      hasName: !!profileData.name,
      hasPhone: !!profileData.phone,
      hasAddress: !!profileData.address
    })

    const response = await apiClient.put('/auth/profile', profileData)

    logger.info('Profile update successful', {
      userId: response.data.data?.user?.id
    })

    return response.data
  } catch (error) {
    logger.error('Profile update failed', {
      error: error.message,
      statusCode: error.response?.status
    })
    throw normalizeError(error, 'Error al actualizar perfil')
  }
}

/**
 * Request password reset with enhanced logging (Task 2.2)
 */
const forgotPassword = async (email) => {
  try {
    logger.info('Password reset request initiated', { email })

    const response = await authClient.post('/auth/forgot-password', { email })

    logger.info('Password reset request successful', { email })

    return response.data
  } catch (error) {
    logger.error('Password reset request failed', {
      email,
      error: error.message,
      statusCode: error.response?.status
    })
    throw normalizeError(error, 'Error al solicitar restablecimiento')
  }
}

/**
 * Reset password with token and enhanced logging (Task 2.2)
 */
const resetPassword = async (token, newPassword) => {
  try {
    logger.info('Password reset attempt initiated', {
      tokenPresent: !!token
    })

    const response = await authClient.post('/auth/reset-password', {
      token,
      password: newPassword,
      confirmPassword: newPassword
    })

    logger.info('Password reset successful')

    return response.data
  } catch (error) {
    logger.error('Password reset failed', {
      error: error.message,
      statusCode: error.response?.status
    })
    throw normalizeError(error, 'Error al restablecer contraseña')
  }
}

/**
 * Change user password (PROF-005)
 * Requires current password verification
 */
const changePassword = async (currentPassword, newPassword) => {
  try {
    logger.info('Password change initiated')

    const response = await apiClient.put('/auth/change-password', {
      currentPassword,
      newPassword
    })

    logger.info('Password change successful')

    return response.data
  } catch (error) {
    logger.error('Password change failed', {
      error: error.message,
      statusCode: error.response?.status
    })
    throw normalizeError(error, 'Error al cambiar contraseña')
  }
}

/**
 * Verify email with token and enhanced logging (Task 2.2)
 */
const verifyEmail = async (token) => {
  try {
    logger.info('Email verification initiated', {
      tokenPresent: !!token
    })

    const response = await authClient.post('/auth/verify-email', { token })

    logger.info('Email verification successful')

    return response.data
  } catch (error) {
    logger.error('Email verification failed', {
      error: error.message,
      statusCode: error.response?.status
    })
    throw normalizeError(error, 'Error al verificar email')
  }
}

/**
 * Resend initial email verification link
 * POST /api/auth/resend-verification
 * Always returns 200 (anti-enumeration). No auth required.
 */
const resendVerification = async (email) => {
  try {
    logger.info('Resend verification email initiated', { email })
    const response = await authClient.post('/auth/resend-verification', { email })
    logger.info('Resend verification email request sent')
    return response.data
  } catch (error) {
    logger.error('Resend verification email failed', {
      error: error.message,
      statusCode: error.response?.status
    })
    throw normalizeError(error, 'Error al reenviar email de verificación')
  }
}

/**
 * Request email change with verification (PROF-004)
 * Sends verification email to new address
 */
const requestEmailChange = async (newEmail) => {
  try {
    logger.info('Email change request initiated', {
      newEmail
    })

    const response = await apiClient.post('/auth/change-email', { newEmail })

    logger.info('Email change request successful', {
      pendingEmail: response.data.data?.pendingEmail
    })

    return response.data
  } catch (error) {
    logger.error('Email change request failed', {
      newEmail,
      error: error.message,
      statusCode: error.response?.status
    })
    throw normalizeError(error, 'Error al solicitar cambio de email')
  }
}

/**
 * Cancel pending email change (PROF-004)
 */
const cancelEmailChange = async () => {
  try {
    logger.info('Cancel email change initiated')

    const response = await apiClient.delete('/auth/change-email')

    logger.info('Email change cancelled successfully')

    return response.data
  } catch (error) {
    logger.error('Cancel email change failed', {
      error: error.message,
      statusCode: error.response?.status
    })
    throw normalizeError(error, 'Error al cancelar cambio de email')
  }
}

/**
 * Resend email change verification (PROF-004)
 */
const resendEmailChangeVerification = async () => {
  try {
    logger.info('Resend email change verification initiated')

    const response = await apiClient.post('/auth/change-email/resend')

    logger.info('Email change verification resent successfully')

    return response.data
  } catch (error) {
    logger.error('Resend email change verification failed', {
      error: error.message,
      statusCode: error.response?.status
    })
    throw normalizeError(error, 'Error al reenviar verificacion de email')
  }
}

/**
 * Verify email change with token (PROF-004)
 * Called from verification link in email
 */
const verifyEmailChange = async (token) => {
  try {
    logger.info('Email change verification initiated', {
      tokenPresent: !!token
    })

    const response = await authClient.get(`/auth/verify-email-change?token=${token}`)

    logger.info('Email change verification successful')

    return response.data
  } catch (error) {
    logger.error('Email change verification failed', {
      error: error.message,
      statusCode: error.response?.status
    })
    throw normalizeError(error, 'Error al verificar cambio de email')
  }
}

/**
 * Check session validity with enhanced logging (Task 2.2)
 * New method for Task 1.1 session endpoint integration
 */
const checkSession = async () => {
  return withRetry(async () => {
    logger.info('Session validation check initiated')

    const response = await apiClient.get('/auth/session')

    logger.info('Session validation successful', {
      userId: response.data.data?.user?.id,
      sessionValid: response.data.data?.authenticated
    })

    return response.data.data // Extract data from success response
  }, 2, 1000) // Max 2 retries with 1s base delay
    .catch(error => {
      logger.error('Session validation failed', {
        error: error.message,
        statusCode: error.response?.status
      })
      throw normalizeError(error, 'Error al validar sesión')
    })
}

/**
 * Check if email already exists - validates before registration payment
 * AUTH-008: Email validation before payment step
 */
const checkEmail = async (email) => {
  try {
    logger.info('Checking if email exists', { email })
    const response = await authClient.post('/auth/check-email', { email })
    return response.data.data
  } catch (error) {
    logger.error('Email check failed', { email, error: error.message })
    throw normalizeError(error, 'Error al verificar email')
  }
}

// ==========================================
// UTILITY FUNCTIONS
// ==========================================

/**
 * Enhanced retry utility with exponential backoff (Task 2.2)
 * Circuit breaker pattern for resilient API calls
 */
const withRetry = async (operation, maxRetries = 3, baseDelay = 1000) => {
  let lastError

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await operation()

      if (attempt > 0) {
        logger.info('Operation succeeded after retry', {
          attempt,
          operation: operation.name || 'unknown'
        })
      }
      return result
    } catch (error) {
      lastError = error
      const isRetryable = isRetryableError(error)

      logger.warn('Operation failed, checking retry eligibility', {
        attempt: attempt + 1,
        maxRetries,
        isRetryable,
        error: error.message,
        statusCode: error.response?.status
      })

      if (attempt === maxRetries || !isRetryable) {
        break
      }

      const delay = baseDelay * Math.pow(2, attempt)
      logger.info('Retrying operation with exponential backoff', {
        attempt: attempt + 1,
        delay: Math.round(delay)
      })

      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }

  throw lastError
}

/**
 * Check if error is retryable for withRetry utility
 */
const isRetryableError = (error) => {
  const status = error.response?.status
  const code = error.code

  // Network errors
  if (code === 'NETWORK_ERROR' || code === 'ECONNABORTED') {
    return true
  }

  // Server errors (5xx)
  if (status >= 500) {
    return true
  }

  // Rate limiting
  if (status === 429) {
    return true
  }

  // Not retryable for client errors (4xx except 429)
  return false
}

/**
 * Normalize API errors for consistent error handling
 * Preserves errorCode and details from backend response
 */
const normalizeError = (error, defaultMessage = 'Error desconocido') => {
  const apiError = error.response?.data

  // Determine the message with priority
  let message = defaultMessage
  if (apiError?.message) {
    message = apiError.message
  } else if (apiError?.error) {
    message = apiError.error
  } else if (error.message) {
    message = error.message
  }

  // Create error with enhanced properties
  const normalizedErr = new Error(message)

  // Preserve errorCode from backend
  normalizedErr.errorCode = apiError?.errorCode || apiError?.code || null

  // Preserve validation details array
  normalizedErr.details = apiError?.details || []

  // Preserve HTTP status
  normalizedErr.status = error.response?.status || null

  return normalizedErr
}

// ==========================================
// EXPORTS
// ==========================================

export default {
  // Core authentication
  login,
  register,
  registerTesting,
  logout,
  refreshTokens,
  refreshToken: refreshTokens, // Alias for backward compatibility
  checkSession,
  checkEmail,

  // Profile management
  getProfile,
  updateProfile,

  // Password management
  forgotPassword,
  resetPassword,
  changePassword,

  // Email verification
  verifyEmail,
  resendVerification,

  // Email change (PROF-004)
  requestEmailChange,
  cancelEmailChange,
  resendEmailChangeVerification,
  verifyEmailChange,

  // Utility functions
  getCsrfToken,
  clearCsrfToken,

  // Enhanced utilities (Task 2.2)
  withRetry,
  normalizeError,

  // Clients for testing (not recommended for production use)
  authClient,
  apiClient,

  // Error utility methods for backward compatibility
  isAuthError: (error) => error.response?.status === 401,
  isValidationError: (error) => error.response?.status === 422,
}